"use client";

import { motion } from "motion/react";
import { BookOpen, Calendar, School } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function PublicEducationDisplay() {
  const { profileData } = usePublicProfileStore();
  const data = profileData?.education_section;
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!data || data.length === 0) return null;

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-emerald-400/50 shadow-2xl backdrop-blur-xl">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-15 -right-20 h-36 w-36 rounded-full bg-gradient-to-br from-emerald-400 to-green-600" />
        <div className="absolute -bottom-20 -left-20 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
      </div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-emerald-500/20 p-2">
            <Icon
              className="text-emerald-400"
              height="1.5rem"
              icon="mdi:book-education-outline"
              width="1.5rem"
            />
          </div>
          {t("profile:educationSection.title")}
        </motion.h2>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.map((education, index) => (
          <motion.div
            key={education.id}
            animate={{ y: 0, opacity: 1 }}
            className="rounded-2xl border border-emerald-300/20 bg-emerald-500/10 p-4 backdrop-blur-sm transition-colors hover:bg-emerald-500/15"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="flex items-start space-x-4">
              <div className="mt-1 rounded-xl bg-emerald-500/20 p-2">
                <School className="h-5 w-5 text-emerald-400" />
              </div>
              <div className="ml-2! space-y-1">
                <h3 className="text-accent-foreground text-lg font-semibold">
                  {education.institution_name}
                </h3>
                <div className="flex gap-2">
                  {education.degree && (
                    <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/20 px-3 py-1">
                      <p className="text-sm font-medium text-emerald-300">{education.degree}</p>
                    </div>
                  )}
                  {education.field_of_study && (
                    <p className="text-muted-foreground flex items-center text-sm">
                      <BookOpen className="mr-2 h-4 w-4" />
                      {education.field_of_study}
                    </p>
                  )}
                </div>
                {(education.start_date || education.end_date) && (
                  <p className="text-muted-foreground flex items-center text-sm">
                    <Calendar className="mr-2 h-4 w-4" />
                    {education.start_date && education.start_date} {education.end_date && " - "}
                    {education.end_date && education.end_date}
                  </p>
                )}
                {education.description && (
                  <div className="mt-2 rounded-xl bg-emerald-500/5 p-3">
                    <p className="text-muted-foreground mb-1 text-xs font-medium">
                      {t("profile:educationSection.display.description")}
                    </p>
                    <p className="text-secondary-foreground text-sm">{education.description}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </CardContent>
    </Card>
  );
}
